/**
 * @fileoverview CAT (Category) question component for rendering category questions
 *
 * @example
 * // Usage in component:
 * import { CATQuestion } from './CAT'
 *
 * <CATQuestion onSubmit={onSubmit} question={question} errors={errors} />
 */

import { useContext, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { MiddlewareContext } from '../../MiddlewareContext'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { StreamingRecorder, StreamingState } from '../StreamingRecorder/StreamingRecorder'
import { Text } from '../typography/Typography'
import css from './CAT.module.css'
import { Instructions } from './sections/Instructions'
import { Title } from './sections/Title'

/**
 * @function getTitle
 * @description Helper function to get the title based on the streaming state
 * @param {Novoic.AudioQuestionBody} body - The body of the audio question
 * @param {StreamingState} state - The current streaming state
 * @returns {string} The title to display
 */
function getTitle(body: Novoic.AudioQuestionBody, state: StreamingState): string {
    switch (state) {
        case StreamingState.IDLE:
        case StreamingState.LOADING:
            return body.title
        case StreamingState.PLAYING:
            return body.titlePlaying
        case StreamingState.STREAMING:
            return body.titleRecording
        case StreamingState.PROCESSING:
        default:
            return ''
    }
}

/**
 * @function CATQuestion
 * @description Component for rendering category question with streaming recorder
 * @param {Novoic.AudioQuestionProps} props - The props for the component
 * @returns {JSX.Element} The CATQuestion component
 */
export function CATQuestion({ onSubmit, question, errors }: Novoic.AudioQuestionProps) {
    const { id, question: body } = question
    const {
        instructions,
        subtext,
        audio,
        recordingStartMode,
        recordingStopMode,
        maxRecordingTime,
    } = body

    const [attempt, setAttempt] = useState<number>(1)
    const { exit } = useContext(MiddlewareContext)
    const [state, setState] = useState<StreamingState>(StreamingState.IDLE)
    const { t } = useTranslation()

    /**
     * Submit when streaming is finished
     */
    function onStreamingEnded(recording: string) {
        onSubmit(id, { recording })
    }

    /**
     * Change the state of the streaming so UI can be updated based on it
     */
    function onStateChange(state: StreamingState) {
        setState(state)
    }

    /**
     * Reset the state of the audio question component
     */
    useEffect(() => {
        if (errors && Object.keys(errors).length > 0) {
            setState(StreamingState.IDLE)
            setAttempt((current) => current + 1)
        }
    }, [errors])

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title flexible>{getTitle(body, state)}</Title>
            {subtext && <Text>{subtext}</Text>}

            <StreamingRecorder
                attempt={attempt}
                onStreamingEnded={onStreamingEnded}
                onStateChange={onStateChange}
                onExit={exit}
                audio={audio}
                titles={{
                    recording: t('streaming.recordingAutomatic'),
                }}
                recordingStartMode={recordingStartMode}
                recordingStopMode={recordingStopMode}
                maxRecordingTime={maxRecordingTime}
            />

            {(state === StreamingState.IDLE || state === StreamingState.LOADING) && (
                <Instructions instructions={instructions} />
            )}

            <div className={css.progress}>
                <DesktopProgress />
            </div>
        </div>
    )
}
